import { Stack, Text, showToast } from "@certn/cascadia";
import { Card, Loader, SectionMessage } from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";
import { Schemas } from "../../types";
import { Formik, FormikProps } from "formik";
import { cvReviewSchema, datesChanged, VerificationType } from "./utils";
import { useClientPortalAPI, useSubmit } from "@certn/api";
import { CVReviewEmploymentExtraction } from "./CVReviewEmploymentExtraction";
import { CVReviewEducationExtraction } from "./CVReviewEducationExtraction";
import debounce from "lodash/debounce";
import { Dispatch, SetStateAction, useMemo } from "react";
import { ValidationError } from "yup";

type CVReviewExtractionProps = {
  cv_review_id: string;
  templateId?: string;
  verificationType?: VerificationType;
  setDisableCompletion: Dispatch<SetStateAction<boolean>>;
};

export const CVReviewExtraction = ({
  cv_review_id,
  templateId,
  verificationType,
  setDisableCompletion,
}: CVReviewExtractionProps) => {
  const { t } = useTranslation();

  const {
    data: cv_review,
    error,
    isLoading,
    mutate: refetchCvReview,
  } = useClientPortalAPI(
    `/api/internal/client-portal/cv-review/${cv_review_id}/`,
    {
      template_id: templateId,
      verification_type: verificationType,
    },
    {
      onError: () => {
        setDisableCompletion(true);
      },
      onSuccess: () => {
        setDisableCompletion(false);
      },
    }
  );

  const { submit: updateCVReview } = useSubmit(
    `/api/internal/client-portal/cv-review/${cv_review_id}/update/`,
    {
      throwErrors: true,
      showToastOnError: true,
    }
  );

  const debouncedUpdate = useMemo(
    () =>
      debounce(
        async (
          confirmed_data: Schemas["CVExtractionResult"],
          prev_confirmed_data: Schemas["CVExtractionResult"] | undefined
        ) => {
          await updateCVReview(confirmed_data, { method: "PUT" });

          // When dates change, some items might not be excluded anymore and we want the UI to reflect that
          const areDatesChanged = datesChanged({
            prev: prev_confirmed_data,
            next: confirmed_data,
          });

          if (areDatesChanged) {
            await refetchCvReview();
          }
        },
        1000
      ),
    [updateCVReview, refetchCvReview]
  );

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t(
          "An error occurred while loading extracted CV Review data. Please try again later."
        )}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  const handleUpdate = async (input: {
    confirmed_data: Schemas["CVExtractionResult"];
    prev_confirmed_data?: Schemas["CVExtractionResult"];
    setValues: FormikProps<
      Pick<Schemas["CVReviewResponse"], "confirmed_data">
    >["setValues"];
  }) => {
    const { confirmed_data, setValues, prev_confirmed_data } = input;

    try {
      cvReviewSchema.validateSync({ confirmed_data }, { abortEarly: false });

      setValues({ confirmed_data });
      debouncedUpdate(confirmed_data, prev_confirmed_data);
      setDisableCompletion(false);
    } catch (error) {
      setValues({ confirmed_data });
      setDisableCompletion(true);

      if (error instanceof ValidationError) {
        const errors = error.errors;

        showToast(
          t("Invalid: {{errors}}", {
            errors:
              errors.length === 1 ? errors[0] : JSON.stringify(error.errors),
          }),
          { type: "error" }
        );
      }
    }
  };

  return (
    <Formik<Pick<Schemas["CVReviewResponse"], "confirmed_data">>
      enableReinitialize
      validateOnMount
      initialValues={{
        confirmed_data: cv_review
          ? cv_review.confirmed_data
          : {
              employments: [],
              educations: [],
            },
      }}
      validationSchema={cvReviewSchema}
      onSubmit={(_, { setSubmitting }) => {
        setSubmitting(false);
      }}
    >
      {({ values, setValues }) => {
        const data =
          cv_review && cv_review.data
            ? cv_review.data
            : {
                employments: [],
                educations: [],
              };
        const employments = values.confirmed_data?.employments || [];
        const educations = values.confirmed_data?.educations || [];

        return (
          <Stack gap="space-4">
            {employments.length > 0 && (
              <Card type="large" border="border-base" padding="space-4">
                <Stack gap="space-6">
                  <Text style="global/heading-medium">
                    {t("Review extracted employment information")}
                  </Text>
                  {employments.map((employment, index) => (
                    <CVReviewEmploymentExtraction
                      key={`employment-${index}`}
                      employment={employment}
                      originalEmployment={data.employments[index]}
                      handleEmploymentItemChange={async (item) => {
                        const confirmed_data = {
                          educations,
                          employments: employments.map((e, i) =>
                            i === index ? item : e
                          ),
                        };

                        handleUpdate({
                          confirmed_data,
                          setValues,
                          prev_confirmed_data:
                            values.confirmed_data || undefined,
                        });
                      }}
                    />
                  ))}
                </Stack>
              </Card>
            )}
            {educations.length > 0 && (
              <Card type="large" border="border-base" padding="space-4">
                <Stack gap="space-6">
                  <Text style="global/heading-medium">
                    {t("Review extracted education information")}
                  </Text>
                  {educations.map((education, index) => (
                    <CVReviewEducationExtraction
                      key={`education-${index}`}
                      education={education}
                      originalEducation={data.educations[index]}
                      handleEducationItemChange={async (item) => {
                        const confirmed_data = {
                          employments,
                          educations: educations.map((e, i) =>
                            i === index ? item : e
                          ),
                        };

                        handleUpdate({
                          confirmed_data,
                          setValues,
                          prev_confirmed_data:
                            values.confirmed_data || undefined,
                        });
                      }}
                    />
                  ))}
                </Stack>
              </Card>
            )}
          </Stack>
        );
      }}
    </Formik>
  );
};
